import React from "react";
import { Table, Button, Tag, Empty, Dropdown } from "antd";
import {
    EyeOutlined,
    EditOutlined,
    DeleteOutlined,
    EllipsisOutlined,
    StopOutlined
} from "@ant-design/icons";

import { formatDate } from "../utils/formatDate";

function ContractTable({
    contracts,
    openEdit,
    setPreviewId,
    setDeleteConfirm,
    loading,
    params,
    setParams,
    isAdmin = true,
    checkPermission,
    onOpenTerminate,
}) {
    const dataSource = contracts?.data || (Array.isArray(contracts) ? contracts : []);
    const total = contracts?.total || dataSource.length;

    const columns = [
        {
            title: "STT",
            key: "index",
            width: 60,
            render: (_, __, index) => (
                <span style={{ color: "#94a3b8", fontSize: 13 }}>
                    {(params.page - 1) * params.per_page + index + 1}
                </span>
            ),
        },
        {
            title: "Mã HĐ",
            dataIndex: "code",
            key: "code",
            width: 140,
            render: (value) => (
                <span style={{ fontWeight: 600, fontSize: 13, color: "#1e293b" }}>
                    {value || "—"}
                </span>
            ),
        },
        {
            title: "Họ tên nhân sự",
            dataIndex: "employeeName",
            key: "employeeName",
            render: (value) => (
                <span style={{ fontWeight: 700, fontSize: 14 }}>
                    {value || "—"}
                </span>
            ),
        },
        {
            title: "Loại hợp đồng",
            dataIndex: "type",
            key: "type",
            width: 160,
            render: (type) => (
                <Tag
                    color={type === "labor" ? "blue" : "purple"}
                    style={{
                        borderRadius: 20,
                        padding: "4px 12px",
                        fontWeight: 600,
                    }}
                >
                    {type === "labor" ? "👔 HĐ Lao động" : "🤝 HĐ Cộng tác viên"}
                </Tag>
            ),
        },
        {
            title: "Chức vụ",
            key: "position",
            render: (_, record) => (
                <span style={{ fontSize: 13, color: "#475569" }}>
                    {record.position || "—"}
                </span>
            ),
        },
        {
            title: "Ngày bắt đầu",
            key: "startDate",
            render: (_, record) => (
                <span style={{ fontSize: 13, color: "#475569" }}>
                    {formatDate(record.startDate)}
                </span>
            ),
        },
        {
            title: "Ngày kết thúc",
            key: "endDate",
            render: (_, record) => (
                <span style={{ fontSize: 13, color: "#475569" }}>
                    {record.endDate ? formatDate(record.endDate) : "Không thời hạn"}
                </span>
            ),
        },
        {
            title: "Trạng thái & Hiệu lực",
            key: "sign_status",
            width: 175,
            render: (_, record) => {
                if (record.status === "terminated") {
                    return (
                        <div>
                            <Tag color="error" style={{ borderRadius: 16, padding: "3px 10px", fontWeight: 700 }}>
                                🔴 Đã kết thúc
                            </Tag>
                            {record.endDate && (
                                <div style={{ fontSize: 11, color: "#64748b", marginTop: 2, paddingLeft: 4 }}>
                                    Hết hạn: {formatDate(record.endDate)}
                                </div>
                            )}
                        </div>
                    );
                }

                const isEmployeeSigned = !!(record.employeeSignature || record.employee_signature);
                const isManagerSigned = !!(record.managerSignature || record.manager_signature);

                if (isEmployeeSigned && isManagerSigned) {
                    return (
                        <div>
                            <Tag color="success" style={{ borderRadius: 16, padding: "3px 10px", fontWeight: 700 }}>
                                🟢 Đang hiệu lực
                            </Tag>
                            <div style={{ fontSize: 11, color: "#16a34a", marginTop: 2, paddingLeft: 4 }}>
                                ✓ Đã ký 2 bên & đóng dấu
                            </div>
                        </div>
                    );
                }

                if (isEmployeeSigned && !isManagerSigned) {
                    return (
                        <div>
                            <Tag color="purple" style={{ borderRadius: 16, padding: "3px 10px", fontWeight: 700 }}>
                                🟣 Chờ Sếp duyệt & đóng dấu
                            </Tag>
                            <div style={{ fontSize: 11, color: "#7c3aed", marginTop: 2, paddingLeft: 4 }}>
                                NV đã ký OTP
                            </div>
                        </div>
                    );
                }

                return (
                    <div>
                        <Tag color="orange" style={{ borderRadius: 16, padding: "3px 10px", fontWeight: 700 }}>
                            🟠 Đang chờ 2 bên ký
                        </Tag>
                        <div style={{ fontSize: 11, color: "#d97706", marginTop: 2, paddingLeft: 4 }}>
                            Chưa ký bên A & B
                        </div>
                    </div>
                );
            },
        },
        {
            title: "Tiến trình ký",
            key: "signature_progress",
            width: 160,
            render: (_, record) => {
                const hasEmpSig = !!(record.employeeSignature || record.employee_signature);
                const hasMgrSig = !!(record.managerSignature || record.manager_signature);

                return (
                    <div style={{ fontSize: 12 }}>
                        <div style={{ color: hasEmpSig ? "#16a34a" : "#64748b", fontWeight: hasEmpSig ? 600 : 400 }}>
                            {hasEmpSig ? "✓ Bên B (NV): Đã ký OTP" : "○ Bên B (NV): Chưa ký"}
                        </div>
                        <div style={{ color: hasMgrSig ? "#dc2626" : "#64748b", fontWeight: hasMgrSig ? 600 : 400 }}>
                            {hasMgrSig ? "★ Bên A (Sếp): Đã đóng dấu" : "○ Bên A (Sếp): Chưa ký"}
                        </div>
                    </div>
                );
            },
        },
        {
            title: "Thao tác",
            key: "actions",
            width: 90,
            align: "center",
            render: (_, record) => {
                const recordId = record.id || record.ID;
                const isEmployeeSigned = !!(record.employeeSignature || record.employee_signature);
                const isManagerSigned = !!(record.managerSignature || record.manager_signature);
                const hasAnySignature = isEmployeeSigned || isManagerSigned;
                const isFullySigned = isEmployeeSigned && isManagerSigned;

                const isTerminated = record.status === "terminated";
                const canEdit = (isAdmin || (checkPermission && checkPermission("contract.update"))) && !hasAnySignature && !isTerminated;
                const canDelete = (isAdmin || (checkPermission && checkPermission("contract.delete"))) && !isFullySigned && !isTerminated;
                const canTerminate = (isAdmin || (checkPermission && checkPermission("contract.terminate"))) && !isTerminated;

                const items = [
                    {
                        key: "preview",
                        icon: <EyeOutlined style={{ color: "#0284c7", fontSize: 15 }} />,
                        label: (
                            <span style={{ fontWeight: 600, color: "#0284c7" }}>
                                {isFullySigned
                                    ? "Xem hợp đồng & Chứng thư"
                                    : (isAdmin ? "Xem trước & Ký duyệt" : "Xem hợp đồng & Ký nhận")}
                            </span>
                        ),
                        onClick: () => setPreviewId(recordId),
                    },
                    ...(canEdit ? [
                        {
                            key: "edit",
                            icon: <EditOutlined style={{ color: "#1677ff", fontSize: 15 }} />,
                            label: <span>Chỉnh sửa hợp đồng</span>,
                            disabled: hasAnySignature,
                            onClick: () => openEdit(record),
                        },
                    ] : []),
                    ...(canTerminate ? [
                        {
                            type: "divider",
                        },
                        {
                            key: "terminate",
                            icon: <StopOutlined style={{ color: "#e11d48", fontSize: 15 }} />,
                            label: <span style={{ color: "#e11d48", fontWeight: 600 }}>Kết thúc hợp đồng</span>,
                            onClick: () => onOpenTerminate && onOpenTerminate(record),
                        },
                    ] : []),
                    ...(canDelete ? [
                        {
                            type: "divider",
                        },
                        {
                            key: "delete",
                            icon: <DeleteOutlined style={{ color: "#dc2626", fontSize: 15 }} />,
                            label: <span style={{ color: "#dc2626" }}>Xóa hợp đồng</span>,
                            danger: true,
                            onClick: () => setDeleteConfirm(recordId),
                        },
                    ] : []),
                ];

                return (
                    <Dropdown
                        menu={{ items }}
                        trigger={["click"]}
                        placement="bottomRight"
                        arrow
                    >
                        <Button
                            type="text"
                            icon={<EllipsisOutlined style={{ fontSize: 22, color: "#334155" }} />}
                            style={{
                                width: 34,
                                height: 34,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: 6,
                                border: "1px solid #cbd5e1",
                                background: "#f8fafc",
                                cursor: "pointer",
                                transition: "all 0.2s",
                            }}
                            title="Thao tác"
                        />
                    </Dropdown>
                );
            },
        },
    ];

    return (
        <Table
            columns={columns}
            loading={loading}
            dataSource={dataSource}
            rowKey={(r) => r.id || r.ID}
            scroll={{ x: 'max-content' }}
            pagination={{
                current: params.page,
                pageSize: params.per_page,
                total: total,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50"],
                onChange: (page, size) => {
                    setParams((prev) => ({ ...prev, page, per_page: size }));
                },
                showTotal: (totalCount, range) =>
                    `${range[0]}-${range[1]} / ${totalCount} hợp đồng`,
            }}
            locale={{
                emptyText: (
                    <Empty description="Không tìm thấy hợp đồng nào" />
                ),
            }}
            bordered
        />
    );
}

export default React.memo(ContractTable);
